import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { useEffect, useRef, useState } from 'react';
import {
  getCommunityMetricStats,
  getCommunityTimeline,
  getMentionsStatsList,
  getMentionsStatsTimeline,
} from '@op/services/keyMetrics';
import {
  dealWithCommunityChart,
  dealWithCommunityPieChart,
  dealWithMentionsSentimentChart,
  dealWithMentionsSentimentList,
  dealWithSentimentSmallChart,
} from '../utils';
import { getCurrentColumns } from '../Community/constants';
import {
  ChartOptionsMapType,
  getRealXAxisValue,
} from '../MentionsSentiment/MetricsChart/utils';
import { CommonColumnsType } from '@op/types/table';
import { channelColorMap } from '@op/constants';
import moment from 'moment';
import { getOverviewMentionsSentiment } from '@op/services';

/**
 * 获取Community的所有图表数据
 * @param filter
 * @returns
 */
export function useKeyMetricsCommunityChart({ filter }: any) {
  const [chartRecord, setChartRecord] = useState<{
    xAxisData?: any;
    yAxisData?: any;
    tableData?: Array<any>;
    tableColumns?: CommonColumnsType[];
    empty?: boolean;
  }>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  function requestChart() {
    if (!filter.edition_unified_id) return;
    setError(false);
    setChartRecord({
      xAxisData: {},
      yAxisData: {},
      tableData: [],
      tableColumns: [],
      empty: true,
    });
    wrapperRequest(cancelFunc =>
      getCommunityTimeline(
        { ...filter, by_type: filter.by_type === 'total' ? '' : filter.by_type },
        cancelFunc,
      )
        .then((data: any) => {
          const chartConfig = getCurrentColumns({
            tab: filter.tab_type,
            keyMetric: filter.metrics[0],
            byTypeKeyMetric: filter.by_type,
          });

          const rawKeys = data.list || Object.keys(data.list_stats || {});
          const keys = rawKeys?.map((item: string) => {
            const header = item === 'others' ? 'Others' : item;
            const color = channelColorMap[item];
            return { header, key: item, color };
          });
          const list =
            typeof chartConfig === 'function' ? chartConfig(keys) : chartConfig;
          const chart = dealWithCommunityChart(data, filter, list);

          setChartRecord(chart);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestChart();
  }, [filter]);

  return { data: chartRecord, loading, error, refetch: requestChart };
}

/**
 * 获取Mentions & Sentiment 的指标数据
 * @param filter
 * @param formatData
 * @param extraData
 * @returns
 */
export function useKeyMetricsMentionsList({ filter, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  function requestChart() {
    if (!filter.edition_unified_id) return;
    setError(false);
    wrapperRequest(cancelFunc =>
      getMentionsStatsList(filter, cancelFunc)
        .then((data: any) => {
          const formattedData = dealWithMentionsSentimentList(
            data,
            filter,
            extraData,
          );
          setRecords(formattedData);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestChart();
  }, [filter]);

  return { data: records, loading, error, refetch: requestChart };
}

type EventSettingType = {
  bubbleEvent: { [key: string]: Array<any> };
  showEventAxis: boolean;
  showEventBubbles: boolean;
};

/**
 * 获取Mentions & Sentiment 的图表数据
 * @param filter
 * @param extraData
 * @returns
 */
export function useKeyMetricsMentionsChart({
  filter,
  extraData,
  isRatingRatio,
  needEventData = true,
}: {
  filter: any;
  extraData: ChartOptionsMapType;
  isRatingRatio?: boolean;
  needEventData?: boolean;
}) {
  const [chartEventSetting, setChartEventSetting] = useState<EventSettingType>({
    bubbleEvent: {},
    showEventAxis: true,
    showEventBubbles: true,
  });
  const [chartRecord, setChartRecord] = useState<{
    xAxisData?: any;
    yAxisData?: any;
    tableData?: any;
    empty?: boolean;
  }>({});

  const [eventData, setEventData] = useState({
    eventList: {}, // 选择器渲染的数据
    chartRenderEventList: {}, // 渲染图表event的数据
    format: '', // 格式化的时间格式
    isEmpty: true,
  });

  const [error, setError] = useState(false);
  const { loading, wrapperRequest, setLoading } = useCancelRequestHook();

  /**
   * 处理周数据，将多个单日数据并入周内
   * @param chartRenderEventList
   * @param filter
   * @param data
   * @returns
   */
  const getChartRenderEventList = (
    chartRenderEventList: any,
    filter: any,
    data: any,
  ) => {
    if (filter?.date_type === 'weekly') {
      const resultCommon: any = {};
      Object.keys(chartRenderEventList || {}).forEach(key => {
        const item = chartRenderEventList[key];
        const realKey = getRealXAxisValue({
          xAxis: key,
          dateType: filter.date_type,
          xAxisData: data?.xAxisData,
          format: data?.format,
        });
        if (!resultCommon[realKey]) {
          resultCommon[realKey] = [];
        }

        resultCommon[realKey].push(item);
      });

      const result: {
        [key: string]: {
          count: number;
          event_list: { [key: string]: Array<any> };
        };
      } = {};
      Object.keys(resultCommon).forEach((key: string) => {
        const elements = resultCommon[key];
        elements.forEach((item: any) => {
          const { count, event_list } = item || {};
          if (!result[key]) {
            result[key] = { count: 0, event_list: {} };
          }
          result[key].count = result[key].count + (count || 0);

          Object.keys(event_list || {}).forEach(eventKey => {
            if (!result[key].event_list[eventKey]) {
              result[key].event_list[eventKey] = [];
            }

            result[key].event_list[eventKey].push(
              ...(event_list[eventKey] || []),
            );
          });
        });
      });
      return result;
    }

    return chartRenderEventList;
  };

  function dealwithEventList(
    data: {
      event_list: any;
      event_timeline: { [key: string]: { count: 1; event_list: Array<any> } };
    },
    chartData: { xAxisData: Array<any>; format: string },
  ): [any, boolean] {
    const chartRenderEventList: any = {};

    let haveEventList = false;

    for (const key in data.event_timeline || {}) {
      const element = data.event_timeline[key];
      const newKey = moment(key).format(chartData.format);
      chartRenderEventList[newKey] = element;
      haveEventList = true;
    }

    return [
      {
        eventList: data.event_list || {}, // 选择器渲染的数据
        chartRenderEventList: getChartRenderEventList(
          chartRenderEventList,
          filter,
          chartData,
        ), // 渲染图表event的数据
        format: chartData.format, // 格式化的时间格式
        isEmpty: Object.keys(chartRenderEventList).length === 0,
      },
      haveEventList,
    ];
  }

  const isChange = useRef(false);
  function changeEventSetting(data: EventSettingType) {
    if (
      data.showEventAxis !== chartEventSetting.showEventAxis ||
      data.showEventBubbles !== chartEventSetting.showEventBubbles
    ) {
      isChange.current = true;
    }

    setChartEventSetting(data);
  }

  function requestChart() {
    if (!filter.edition_unified_id) return;
    setError(false);
    setChartRecord({});
    wrapperRequest(cancelFunc =>
      getMentionsStatsTimeline(
        { ...filter, need_event_data: needEventData },
        cancelFunc,
      )
        .then((data: any) => {
          const chart = dealWithMentionsSentimentChart(
            data,
            filter,
            extraData,
            isRatingRatio,
          );
          const [chartEventList, haveEventList] = dealwithEventList(
            data,
            chart,
          );

          setChartRecord(chart);
          setEventData(chartEventList);
          setChartEventSetting({
            ...chartEventSetting,
            showEventAxis: isChange.current
              ? chartEventSetting.showEventAxis
              : haveEventList,
          });
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestChart();
  }, [filter]);

  return {
    data: chartRecord,
    loading,
    error,
    refetch: requestChart,
    eventData,
    chartEventSetting,
    setLoading,
    changeEventSetting,
  };
}

/**
 * 获取Community的右侧图表的饼图数据
 * @param param0
 * @returns
 */
export function useKeyMetricsCommunityPieChart({ filter, extraData }: any) {
  const [chartRecord, setChartRecord] = useState<{
    xAxisData?: any;
    yAxisData?: any;
    tableData?: any;
    pieData?: any;
    pieTotalData?: any;
    timelineTotal?: any;
  }>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  function requestChart() {
    if (!filter.edition_unified_id) return;
    setError(false);
    setChartRecord({});
    wrapperRequest(cancelFunc =>
      getCommunityMetricStats(filter, cancelFunc)
        .then((data: any) => {
          const chart = dealWithCommunityPieChart(data, filter, extraData);
          setChartRecord(chart);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestChart();
  }, [filter]);

  return { data: chartRecord, loading, error, refetch: requestChart };
}

export function useKeyMetricsSentimentList({ filter, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  function requestChart() {
    if (!filter.edition_unified_id) return;
    setError(false);
    wrapperRequest(cancelFunc =>
      getOverviewMentionsSentiment(filter, cancelFunc)
        .then((data: any) => {
          const formattedData = dealWithSentimentSmallChart(
            data,
            filter,
            extraData,
          );
          setRecords(formattedData);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestChart();
  }, [filter]);

  return { data: records, loading, error, refetch: requestChart };
}

export async function getKeyMetricsCommunityDownloadData({
  filter,
  setDownloadLoading,
}: {
  filter: any;
  setDownloadLoading: (loading: boolean) => void;
}) {
  setDownloadLoading(true);
  try {
    const data = await getCommunityTimeline({
      ...filter,
      by_type: filter.by_type === 'total' ? '' : filter.by_type,
      should_expand_others_data: true,
    });
    const chartConfig = getCurrentColumns({
      tab: filter.tab_type,
      keyMetric: filter.metrics[0],
      byTypeKeyMetric: filter.by_type,
    });

    const rawKeys = data?.list || Object.keys(data?.list_stats || {});
    const keys = rawKeys.map((item: string) => ({
      header: item === 'others' ? 'Others' : item,
      key: item,
      color: channelColorMap[item],
    }));

    const list =
      typeof chartConfig === 'function' ? chartConfig(keys) : chartConfig;

    const downloadData = dealWithCommunityChart(data, filter, list);
    return downloadData;
  } finally {
    setDownloadLoading(false);
  }
}
